import type { NavigationType } from "react-router-dom";

export type RouteNavDirection = "forward" | "back";

/**
 * Relative depth in the kiosk flow. Lower = earlier screen.
 * Used so path-based "back" navigations get the back animation
 * even when they are PUSH/REPLACE (not history POP).
 */
const FLOW_STEP: Record<string, number> = {
  "": 0,
  all: 1,
  idle: 2,
  "choose-portrait": 3,
  "select-people-cartoon": 3,
  "choose-option": 4,
  "select-people": 4,
  "select-media-library": 5,
  "extract-person": 6,
  "select-hobby": 7,
  "hobby-write-line": 8,
  "creating-person-hobby": 9,
  "hobby-result-review": 10,
  "hobby-result-compare": 11,
  "created-person-hobby": 12,
  "save-favorite": 13,
  "upload-image-qr-code": 4,
  "image-transformation": 5,
  "bg-remove": 6,
  "transformation-result": 7,
  "select-art-background": 8,
  "select-fifa-card": 4,
  "upload-fifa-image": 5,
  "enter-profile": 6,
  "fifa-stats-choice": 7,
  "enter-stats": 8,
  "creating-fifa-card": 9,
  "save-fifa-card": 10,
  "fifa-card-compare": 11,
  "print-frame-selection": 20,
  "quick-print-frame-selection": 20,
  "review-cart": 21,
  "qr-scan-to-pay": 22,
  "payment-success": 23,
  "order-confirmation": 24,
  "thank-you": 25,
};

let pendingOverride: RouteNavDirection | null = null;

export function markNavigationBack(): void {
  pendingOverride = "back";
}

export function markNavigationForward(): void {
  pendingOverride = "forward";
}

function routeSegment(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return "";
  // `/:slug/...` → second segment; bare routes use first
  if (parts.length >= 2) return parts[1];
  return parts[0];
}

function flowStep(pathname: string): number {
  const segment = routeSegment(pathname);
  if (Object.prototype.hasOwnProperty.call(FLOW_STEP, segment)) {
    return FLOW_STEP[segment];
  }
  // Unknown slug-only path like /hobby-caricature treated as category root
  return FLOW_STEP[segment] ?? -1;
}

export function resolveRouteTransitionDirection(
  fromPathname: string,
  toPathname: string,
  navigationType: NavigationType
): RouteNavDirection {
  if (pendingOverride) {
    const direction = pendingOverride;
    pendingOverride = null;
    return direction;
  }

  if (navigationType === "POP") {
    return "back";
  }

  const fromStep = flowStep(fromPathname);
  const toStep = flowStep(toPathname);
  const toSegment = routeSegment(toPathname);

  // Returning to category grid / home from deeper in the flow
  if (toPathname === "/" || toSegment === "all") {
    if (fromStep > FLOW_STEP.all) return "back";
  }
  if (toSegment === "idle" && fromStep > FLOW_STEP.idle) {
    return "back";
  }

  if (fromStep >= 0 && toStep >= 0 && fromStep !== toStep) {
    return toStep < fromStep ? "back" : "forward";
  }

  return "forward";
}
